import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'Support', path: '/support' },
        { name: 'Calendar', path: '/calendar' },
        { name: 'News', path: '/news' },
        { name: 'Photos', path: '/photos' },
        { name: 'Marketplace', path: '/marketplace' },
        { name: 'Partners', path: '/partners' },
        { name: 'Contact', path: '/contact' },
    ];

    const isActive = (path) => location.pathname === path;

    const isTransparentPage = ['/', '/about', '/services', '/support'].includes(location.pathname);

    return (
        <nav className={`fixed w-full z-50 transition-all duration-500 ${
            scrolled
                ? 'bg-white shadow-lg'
                : isTransparentPage
                    ? 'bg-transparent'
                    : 'bg-purple-700'
        }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-24">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <Link to="/" className="flex items-center group">
                            <div className="relative h-20 w-20 mr-3">
                                <img
                                    src="/images/ccm.png"
                                    alt="CCM Logo"
                                    className="h-full w-full object-contain transform transition-all
                                             duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-purple-400 rounded-full
                                              animate-ping opacity-20 group-hover:opacity-40" />
                            </div>
                            <span className={`font-bold text-3xl tracking-tight
                                         transition-all duration-300 ${
                                scrolled
                                    ? 'text-purple-900 group-hover:text-purple-700'
                                    : 'text-white group-hover:text-purple-100'
                            }`}>
                                CCM
                            </span>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:block">
                        <div className="ml-10 flex items-center space-x-8">
                            {navItems.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.path}
                                    className={`relative px-3 py-2 text-sm font-semibold tracking-wide
                                              transition-all duration-300 group hover:bg-opacity-10
                                              rounded-full ${
                                        scrolled
                                            ? 'hover:bg-purple-100'
                                            : 'hover:bg-white'
                                    }`}
                                >
                                    <span className={`relative z-10 ${
                                        isActive(item.path)
                                            ? scrolled
                                                ? 'text-purple-700'
                                                : 'text-white font-bold'
                                            : scrolled
                                                ? 'text-gray-700 group-hover:text-purple-700'
                                                : 'text-white/90 group-hover:text-white'
                                    }`}>
                                        {item.name}
                                    </span>
                                    {isActive(item.path) && (
                                        <span className={`absolute bottom-0 left-1/2 w-1/2 h-0.5 
                                                      transform -translate-x-1/2 ${
                                            scrolled
                                                ? 'bg-purple-600'
                                                : 'bg-white'
                                        }`} />
                                    )}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="md:hidden">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className={`inline-flex items-center justify-center p-2 rounded-lg
                                     transition-all duration-300 ${
                                scrolled
                                    ? 'text-gray-700 hover:text-purple-700 hover:bg-purple-50'
                                    : 'text-white hover:text-white hover:bg-white/10'
                            }`}
                        >
                            {isOpen ? (
                                <X className="h-6 w-6" />
                            ) : (
                                <Menu className="h-6 w-6" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <div className={`md:hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                } overflow-hidden`}>
                    <div className="px-2 pt-2 pb-3 space-y-1 bg-white shadow-lg rounded-lg mb-2">
                        {navItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.path}
                                className={`block px-4 py-2 rounded-lg text-base font-medium 
                                          transition-all duration-300 ${
                                    isActive(item.path)
                                        ? 'bg-purple-50 text-purple-700'
                                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                                }`}
                                onClick={() => setIsOpen(false)}
                            >
                                {item.name}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navigation;